import { isRejectedWithValue } from '@reduxjs/toolkit';
import { ErrorMonitoring } from '@ui/services/error-monitoring';

import { ExtraReducerBuilder } from '../../types/shared';
import { WritableQuoteBuilderStateType } from '../../types/state';
import { isFormDirty } from '../selectors/quote-builder-dirty';
import { deleteQuotePriceOptimistically } from '../thunks/delete-quote-price-optimistically';
import { buildIssueId } from '../utilities/issue-id';
import { upsertIssue } from '../utilities/issue-managers';
import { stringifyQuote } from '../utilities/stringify-quote';

type DeleteQuotePriceOptimisticallyFulfilledAction = ReturnType<
  typeof deleteQuotePriceOptimistically.fulfilled
>;

type DeleteQuotePriceOptimisticallyRejectedAction = ReturnType<
  typeof deleteQuotePriceOptimistically.rejected
>;

function quotePriceDeletionSucceeded(
  state: WritableQuoteBuilderStateType,
  action: DeleteQuotePriceOptimisticallyFulfilledAction,
) {
  const wasFormDirty = isFormDirty(state);
  const { quoteLineUuid, quotePriceUuid } = action.meta;

  Object.entries(state.issues).forEach(([issueId, issue]) => {
    if (
      issue.area === 'quotePrice' &&
      issue.quotePriceUuid === quotePriceUuid
    ) {
      delete state.issues[issueId];
    }
  });

  const quoteLine = state.quoteLines[quoteLineUuid];

  /**
   * @NOTE(shawk): This isn't expected to happen because the UI should not allow
   * a user to delete a Quote Line while a Quote Price deletion is pending. That
   * invariant may not hold in the future, intentionally or otherwise, so we
   * capture this case for monitoring.
   */
  if (!quoteLine) {
    ErrorMonitoring.captureException(
      new Error(
        'Quote line not found when handling successful quote price deletion',
      ),
      {
        extra: {
          quoteLineUuid,
          quotePriceUuid,
        },
      },
    );

    return;
  }

  quoteLine.quotePriceUuids = quoteLine.quotePriceUuids.filter(
    (id) => id !== quotePriceUuid,
  );

  delete quoteLine.quotePrices[quotePriceUuid];

  if (!wasFormDirty) {
    state.lastSaveDateTime = new Date();
    state.currentServerData = stringifyQuote(state);
  }
}

function quotePriceDeletionFailed(
  state: WritableQuoteBuilderStateType,
  action: DeleteQuotePriceOptimisticallyRejectedAction,
) {
  if (!action.meta.quotePriceUuid) {
    return;
  }

  const { quoteLineUuid, quotePriceUuid } = action.meta;

  const quoteLine = state.quoteLines[quoteLineUuid];
  const quotePrice = quoteLine?.quotePrices[quotePriceUuid];

  if (!quotePrice) {
    return;
  }

  quotePrice.status = 'deletion-failed';

  upsertIssue(
    state,
    buildIssueId({
      area: 'quotePrice',
      quoteLineUuid,
      quotePriceUuid,
      type: 'rejected',
    }),
    {
      title: 'Failed to Delete Price',
      description: 'Quote price could not be deleted in the database',
      area: 'quotePrice',
      quoteLineUuid,
      quotePriceUuid,
      severity: 'error',
      type: 'rejected',
      action: {
        type: 'unrecoverable',
      },
    },
  );
}

export function deleteSnapQuotePriceReducer(builder: ExtraReducerBuilder) {
  builder.addMatcher(
    deleteQuotePriceOptimistically.fulfilled.match,
    quotePriceDeletionSucceeded,
  );

  builder.addMatcher(
    isRejectedWithValue(deleteQuotePriceOptimistically),
    quotePriceDeletionFailed,
  );
}
